import React, { useRef, useMemo } from "react";
import { useMutation } from "@apollo/client";
import { Stack } from "@mui/material";
import { Formik, Form as FormikForm } from "formik";
import MonitoringHeaderStack from "./MonitoringHeaderStack";
import MonitoringCard from "./MonitoringCard";
import { LoadingButton } from "@mui/lab";
import { SaveMonitoringMutation } from "./queries";
import { useNotifications } from "notifications";
import { PracticeReview } from "practice-reviews";
import { DocType } from "common/DocType";
import { FrequencyOptions, Monitoring, MonitoringInput } from "./models";
import { makeStyles } from "makeStyles";
import { useUnsavedChanges } from "UnsavedChangesProvider";
import RichTextEditor from "common/RichTextEditor";
import { RegistrationCommitteeItem } from "registration-committee-items";
import { DateTime } from "luxon";

const useStyles = makeStyles()({
  headerStack: {
    width: "75%",
    marginTop: "8px",
  },
});

/*
 * NOTE: Monitoring for Practice Reviews is being deprecated.
 * Going forward, monitoring will only be managed through the Registration Committee screen.
 *
 * This component still supports PracticeReview in `reviewOrItem` and the "review" value
 * for `containerType` so that:
 *   1. Historical data can still be displayed for completed or past Practice Reviews.
 *   2. Active monitoring items linked to existing Practice Reviews can continue to be edited
 *      until they are completed.
 *
 * Once the last active monitoring item for a Practice Review is complete, this component
 * can be simplified to only support RegistrationCommitteeItem, and PracticeReview-related
 * props, state, and logic can be removed.
 *
 * User Story: 74164
 * SJI
 */

interface Props {
  reviewOrItem: PracticeReview | RegistrationCommitteeItem;
  containerType: "review" | "item";
}

interface FormValues {
  monitoringFrequencyInDays: number;
  monitoringOccurrencesRemaining: number;
  monitoringSingleHardDeadlineDate?: DateTime | null;
  monitorings: Monitoring[];
  containerMonitoringNotes: string | null;
}

export const MonitoringTab: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles();
  const { reviewOrItem, containerType } = props;
  const notifications = useNotifications();
  const { unsavedChanges, changesSaved } = useUnsavedChanges();

  const [saveMonitoring, { loading }] = useMutation<
    { monitoring: { save: Monitoring[] } },
    { practiceReviewId: number | null; registrationCommitteeItemId: number | null; monitoringInput: MonitoringInput }
  >(SaveMonitoringMutation, {
    onCompleted: () => {
      notifications.success("Monitoring saved.");
      changesSaved();
    },
    onError: (error) => {
      notifications.error(error.message || "An error occurred.");
    },
  });

  const notesContentRetriever = useRef<{ getContentAsHtml: () => string | null }>({ getContentAsHtml: () => null });

  const currentMonitoringPeriod = reviewOrItem.monitorings?.filter((m) => !m.isCompleted)[0];
  const lastCompletedMonitoringDate = reviewOrItem.monitorings?.filter((m) => m.isCompleted).pop()?.completedDate;
  const monitoringLetters = useMemo(
    () =>
      Object.fromEntries(
        reviewOrItem.attachedDocuments.filter((ad) => ad.type === DocType.MonitoringCompleteLetter).map((ad) => [ad.id, ad]),
      ),
    [reviewOrItem.attachedDocuments],
  );
  const initialValues: FormValues = {
    monitoringFrequencyInDays:
      "monitoringSingleHardDeadlineDate" in reviewOrItem && reviewOrItem.monitoringSingleHardDeadlineDate
        ? FrequencyOptions.HardDeadline.value
        : (reviewOrItem.monitoringFrequencyInDays ?? 365),
    monitoringOccurrencesRemaining: reviewOrItem.monitoringOccurrencesRemaining ?? 1,
    monitoringSingleHardDeadlineDate:
      "monitoringSingleHardDeadlineDate" in reviewOrItem && reviewOrItem.monitoringSingleHardDeadlineDate
        ? DateTime.fromISO(reviewOrItem.monitoringSingleHardDeadlineDate)
        : null,
    monitorings: reviewOrItem.monitorings ?? [],
    containerMonitoringNotes: reviewOrItem.monitoringNotes,
  };

  async function save(values: FormValues) {
    const monitoringToEdit = values.monitorings[values.monitorings.length - 1];
    const isMonitoringDeadline = values.monitoringFrequencyInDays === FrequencyOptions.HardDeadline.value;
    const input = {
      practiceReviewId: containerType === "review" ? reviewOrItem.id : null,
      registrationCommitteeItemId: containerType === "item" ? reviewOrItem.id : null,
      monitoringInput: {
        monitoringFrequencyInDays: !isMonitoringDeadline ? values.monitoringFrequencyInDays : null,
        monitoringOccurrencesRemaining: !isMonitoringDeadline ? Number(values.monitoringOccurrencesRemaining) : null,
        monitoringSingleHardDeadlineDate:
          isMonitoringDeadline && values.monitoringSingleHardDeadlineDate ? values.monitoringSingleHardDeadlineDate.toISODate() : null,
        id: monitoringToEdit?.id,
        isPass: monitoringToEdit?.isPass,
        sendToCommitteeOnFail: monitoringToEdit?.sendToCommitteeOnFail,
        monitoringNotes: monitoringToEdit?.monitoringNotes,
        containerMonitoringNotes: notesContentRetriever.current?.getContentAsHtml(),
      } as MonitoringInput,
    };

    await saveMonitoring({ variables: input });
  }

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={save}>
      {({ values, handleSubmit, setFieldValue, isSubmitting }) => (
        <FormikForm onSubmit={handleSubmit} onChange={() => unsavedChanges()}>
          <Stack gap={4}>
            <MonitoringHeaderStack
              direction="row"
              justifyContent="start"
              alignItems="center"
              gap={4}
              monitoringFrequencyInDays={values.monitoringFrequencyInDays}
              monitoringOccurrencesRemaining={values.monitoringOccurrencesRemaining}
              currentMonitoringPeriodDate={currentMonitoringPeriod?.monitoringDate}
              createdDate={reviewOrItem.completedDate}
              monitoringCompleted={!!reviewOrItem.monitoringCompleted}
              monitoringCompletedOnDate={lastCompletedMonitoringDate}
              monitoringSingleHardDeadlineDate={values.monitoringSingleHardDeadlineDate}
              setFieldValue={setFieldValue}
              className={classes.headerStack}
              isSubmitting={isSubmitting}
              lastCompletedMonitoringDate={lastCompletedMonitoringDate}
            />
            <Stack gap={4}>
              {values.monitorings.map((monitoring, index) => (
                <MonitoringCard
                  key={monitoring.id}
                  index={index}
                  monitoring={monitoring}
                  setFieldValue={setFieldValue}
                  monitoringCompletedLetter={
                    monitoring.monitoringCompletedLetterId &&
                    Object.prototype.hasOwnProperty.call(monitoringLetters, monitoring.monitoringCompletedLetterId)
                      ? monitoringLetters[monitoring.monitoringCompletedLetterId]
                      : undefined
                  }
                  monitoringFailedWorkflowReadOnly={!!reviewOrItem.monitoringCompleted || index !== values.monitorings.length - 1}
                  containerType={containerType}
                />
              ))}
            </Stack>
            <RichTextEditor
              label="Notes"
              minHeight="6em"
              html={values.containerMonitoringNotes}
              passContentRetriever={(getContentAsHtml) => {
                notesContentRetriever.current = { getContentAsHtml };
              }}
              reportUnsavedChanges
              hideToolbar
            />
            <Stack justifyContent="end" direction="row">
              <LoadingButton color="primary" variant="contained" type="submit" loading={loading}>
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </FormikForm>
      )}
    </Formik>
  );
};
